import React, { useState } from "react";
import moment from "moment-timezone";
import {
  Comment,
  Avatar,
  Tooltip,
  Popconfirm,
  Form,
  Button,
  Space,
} from "antd";
import {
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import httpClientCron from "../../api/module/httpClientCron";
import TextArea from "antd/lib/input/TextArea";
import TimezoneList from "enum/TimezoneList";
import "./style.scss";
import { USE_GET_CERTIFICATE_COMMENTS } from "./DiscussionBoard";
import { FroalaEdit } from "../../components";

export default function DiscussionBoardCommentReply({
  comment,
  index,
  userProfile,
  certificateUserId,
  isGroupDiscussionBoard,
  certificateClassId,
  s3Hash,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(comment.content);

  let currentTimezone = TimezoneList.find(
    (item) => item.value === userProfile?.timezone
  );
  currentTimezone = currentTimezone?.utc[0] || moment.tz.guess()

  const query = [USE_GET_CERTIFICATE_COMMENTS, certificateClassId];

  const queryClient = useQueryClient();

  const likeReplyMutation = useMutation({
    mutationFn: ({ id, isGroupDiscussionBoard }) => {
      if (isGroupDiscussionBoard) {
        return httpClientCron.patch(
          `private/certificate-group-reply/${id}/like`
        );
      } else {
        return httpClientCron.patch(`private/certificate-reply/${id}/like`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(query);
    },
  });

  const dislikeReplyMutation = useMutation({
    mutationFn: ({ id, isGroupDiscussionBoard }) => {
      if (isGroupDiscussionBoard) {
        return httpClientCron.patch(
          `private/certificate-group-reply/${id}/dislike`
        );
      } else {
        return httpClientCron.patch(`private/certificate-reply/${id}/dislike`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(query);
    },
  });

  const deleteReplyMutation = useMutation({
    mutationFn: ({ id, isGroupDiscussionBoard }) => {
      if (isGroupDiscussionBoard) {
        return httpClientCron.delete(`private/certificate-group-reply/${id}`);
      } else {
        return httpClientCron.delete(`private/certificate-reply/${id}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(query);
    },
  });

  const actions = [];

  if (userProfile.id === comment.CertificateUser.UserId) {
    actions.push(<span onClick={() => setIsEditing(true)}>Edit</span>);
    actions.push(
      <Popconfirm
        title="Delete the comment"
        description="Are you sure to delete this comment?"
        onConfirm={() =>
          deleteReplyMutation.mutate({ id: comment.id, isGroupDiscussionBoard })
        }
        okText="Delete"
        cancelText="Cancel"
      >
        <span>Delete</span>
      </Popconfirm>
    );
  }

  const hasLiked = comment.likes.some(
    (_certificateUserId) => _certificateUserId === certificateUserId
  );

  const hasDisliked = comment.dislikes.some(
    (_certificateUserId) => _certificateUserId === certificateUserId
  );

  const editReplyMutation = useMutation({
    mutationFn: ({ id, data, isGroupDiscussionBoard }) => {
      if (isGroupDiscussionBoard) {
        return httpClientCron.patch(
          `private/certificate-group-reply/${id}/edit`,
          data
        );
      } else {
        return httpClientCron.patch(
          `private/certificate-reply/${id}/edit`,
          data
        );
      }
    },
    onSuccess: () => {
      setIsEditing(false);
      setContent(comment.content);
      queryClient.invalidateQueries(query);
    },
  });

  if (isEditing) {
    return (
      <div
        style={{
          padding: "1rem",
        }}
      >
        <Form.Item>
          {/*<TextArea*/}
          {/*  rows={2}*/}
          {/*  onChange={(e) => setContent(e.target.value)}*/}
          {/*  value={content}*/}
          {/*  placeholder="Add comment"*/}
          {/*/>*/}
          <FroalaEdit
            s3Hash={s3Hash}
            onChange={(e) => setContent(e.html)}
            value={{ html: content }}
          />
        </Form.Item>
        <div
          style={{
            display: "flex",
            justifyItems: "center",
            alignItems: "center",
          }}
        >
          <Button
            htmlType="submit"
            loading={editReplyMutation.isLoading}
            onClick={() =>
              editReplyMutation.mutate({
                data: { content },
                id: comment.id,
                isGroupDiscussionBoard,
              })
            }
            type="primary"
            style={{ marginTop: ".5rem", marginRight: "1rem" }}
          >
            Reply
          </Button>
          <Button
            htmlType="button"
            onClick={() => {
              setIsEditing(false);
              setContent(comment.content);
            }}
            type="secondary"
            style={{ marginTop: ".5rem" }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Comment
      style={{
        borderTop: index === 0 ? "none" : "1px solid #e1e2ee",
        paddingTop: index === 0 ? 0 : "1em",
      }}
      key={comment.id}
      author={
        <span style={{ display: "flex", alignItems: "start" }}>
          <Space>
            <h5>{`${comment.CertificateUser.User.firstName} ${comment.CertificateUser.User.lastName}`}</h5>
            <span style={{ fontSize: "12px", fontWeight: "lighter" }}>
              {moment
                .utc(comment.createdAt)
                .tz(currentTimezone)
                .format("YYYY-MM-DD HH:mm")}
            </span>
          </Space>
        </span>
      }
      avatar={
        <Avatar
          src={comment.CertificateUser.User.img}
          alt={`${comment.CertificateUser.User.firstName} ${comment.CertificateUser.User.lastName}`}
        />
      }
      content={<div dangerouslySetInnerHTML={{ __html: comment.content }} />}
      actions={[
        <Tooltip key="comment-basic-like" title="Like">
          <span
            onClick={() =>
              likeReplyMutation.mutate({
                id: comment.id,
                isGroupDiscussionBoard,
              })
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {hasLiked ? (
              <LikeFilled style={{ width: "1.25rem" }} />
            ) : (
              <LikeOutlined style={{ width: "1.25rem" }} />
            )}
            <span className="comment-action">{comment.likes.length}</span>
          </span>
        </Tooltip>,
        <Tooltip key="comment-basic-dislike" title="Dislike">
          <span
            onClick={() =>
              dislikeReplyMutation.mutate({
                id: comment.id,
                isGroupDiscussionBoard,
              })
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {hasDisliked ? (
              <DislikeFilled style={{ width: "1.25rem" }} />
            ) : (
              <DislikeOutlined style={{ width: "1.25rem" }} />
            )}
            <span className="comment-action">{comment.dislikes.length}</span>
          </span>
        </Tooltip>,
        ...actions,
      ]}
    />
  );
}
